import { FwIconComponent, IconType } from '@flywheel-io/vision';

import { ContainerType } from '../enums/container-type.enum';
import { JobPriority } from '../enums/job-priority.enum';
import { JobState } from '../enums/job-state.enum';
import { File } from './file.model';
import { ParameterValue } from './gear.model';
import { OriginType } from './origin.model';

export interface Job {
  _id: string; // old core
  id?: string; // core-api
  attempt: number;
  batch: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  compute_provider_id: string;
  config: {
    [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  };
  created: string;
  destination: {
    id: string;
    type: string;
  };
  priority?: JobPriority;
  failure_reason?: string;
  gear_id: string;
  gear_info: {
    id: string;
    category: string;
    name: string;
    version: string;
    description?: string;
    capabilities?: string[];
  };
  inputs: JobInput[];
  label: string;
  modified: string;
  origin: {
    type: OriginType;
    id: string | null;
  };
  outputs: File[];
  parents: {
    [container: string]: string | null;
  };
  parent_info?: {
    [container: string]: {
      _id: string;
      label: string;
    } | null;
  };
  previous_job_id?: string;
  produced_metadata: {
    [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  };
  profile: {
    total_input_files: number | null;
    total_input_size_bytes: number | null;
    total_output_files: number | null;
    total_output_size_bytes: number | null;
    preparation_time_ms: number | null;
    elapsed_time_ms: number | null;
    upload_time_ms: number | null;
    total_time_ms: number | null;
    versions: {
      [component: string]: string;
    } | null;
    executor?: {
      name: string;
      host: string | null;
      instance_type: string | null;
      cpu_cores: number;
      gpu: boolean | null;
      memory_bytes: number;
      disk_bytes: number;
      swap_bytes: number;
    };
  };
  related_container_ids: string[];
  request: {
    inputs: {
      location: string;
      type: string;
      uri: string;
      vu: string | null;
    }[];
    outputs: {
      location: string;
      type: string;
      uri: string;
      vu: string | null;
    }[];
    target: {
      command: string[];
      dir: string;
      env: {
        [name: string]: string;
      };
      uid: string | null;
      gid: string | null;
    };
  };
  retried: string | null;
  role_id?: string | null;
  rule_id?: string | null;
  saved_files: string[];
  state: JobState;
  tags: string[];
  transitions: {
    running?: string;
    failed?: string;
    complete?: string;
    cancelled?: string;
  };
}

export interface JobInput {
  id: string; // container ID
  input?: string; // input name/key
  name: string; // file name
  type: ContainerType;
}

// Resolved values and locations for context inputs
export interface JobContextInputs {
  [input: string]: {
    container_type?: ContainerType;
    found: boolean;
    id?: string;
    label?: string;
    value?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  };
}

export interface JobLog {
  fd: number;
  msg: string;
}

export interface JobLogResponse {
  _id: string;
  logs: JobLog[];
}

export interface JobParents {
  group: string;
  project: string;
  subject?: string;
  session?: string;
  acquisition?: string;
  analysis?: string;
}

export interface JobStatus {
  display: string;
  icon: IconType;
  iconColor: ReturnType<FwIconComponent['color']>;
}

interface JobDetailParentInfo {
  _id: string;
  label: string;
}

export interface JobDetailFile {
  ref: {
    id: string; // container _id
    type: string; // container type
    name: string; // file name
  };
  object?: File; // if this is missing, the file has been deleted
}

export interface JobDetail extends Omit<Job, 'inputs' | 'outputs' | 'saved_files'> {
  inputs: {
    [name: string]: JobDetailFile;
  };
  outputs: JobDetailFile[];
  parent_info: {
    group: JobDetailParentInfo;
    project: JobDetailParentInfo;
    subject: JobDetailParentInfo;
    session: JobDetailParentInfo;
    acquisition: JobDetailParentInfo;
    analysis: JobDetailParentInfo;
  };
}

export interface JobBulkActionResult {
  failed: string[];
  success: string[];
}

export interface JobPriorityUpdateActionResult {
  failed: string[];
  modified: number;
}

export interface AddJob {
  config: Record<string, ParameterValue | ParameterValue[]>;
  compute_provider_id?: string;
  destination?: {
    // output location
    type: ContainerType;
    id: string;
  };
  priority?: JobPriority;
  gear_id: string;
  inputs: Record<string, JobInput>;
  tags: string[];
  container_type?: ContainerType;
}

export enum JobColumn {
  State = 'state',
  Priority = 'priority',
  GearName = 'gear_name',
  User = 'user',
  Group = 'group',
  Project = 'project',
  Subject = 'subject',
  Session = 'session',
  Created = 'created',
  Completed = 'completed',
  Tag = 'tag',
  GearRule = 'gear_rule',
  GearRole = 'gear_role',
}
